export const HIRE_WIZARD_STEPS = [
  { id: "company", label: "Company", shortLabel: "Company" },
  { id: "responsibilities", label: "Responsibilities", shortLabel: "Channels" },
  { id: "training", label: "Training", shortLabel: "Training" },
  { id: "organization", label: "Team & Locations", shortLabel: "Team" },
  { id: "tools", label: "Tools", shortLabel: "Tools" },
  { id: "summary", label: "Summary", shortLabel: "Summary" },
] as const;

export type HireWizardStepId = (typeof HIRE_WIZARD_STEPS)[number]["id"];

export const HIRE_WIZARD_STEP_SESSION_KEY = "egpt-hire-wizard-step";
export const HIRE_WIZARD_CONTEXT_SESSION_KEY = "egpt-hire-wizard-context";

export type HireWizardPersistedContext = {
  websiteUrl: string;
  stepId: HireWizardStepId;
};

export function hireWizardStepIndex(stepId: HireWizardStepId): number {
  return HIRE_WIZARD_STEPS.findIndex((s) => s.id === stepId);
}

function normalizeWizardWebsiteKey(url: string): string {
  return url.trim().toLowerCase().replace(/\/$/, "");
}

export function clearHireWizardProgress(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(HIRE_WIZARD_STEP_SESSION_KEY);
    sessionStorage.removeItem(HIRE_WIZARD_CONTEXT_SESSION_KEY);
    localStorage.removeItem(HIRE_WIZARD_STEP_SESSION_KEY);
    localStorage.removeItem(HIRE_WIZARD_CONTEXT_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function readHireWizardStepForWebsite(websiteUrl: string): HireWizardStepId {
  if (typeof window === "undefined") return "company";
  const key = normalizeWizardWebsiteKey(websiteUrl);
  if (!key) return "company";

  try {
    const rawContext =
      sessionStorage.getItem(HIRE_WIZARD_CONTEXT_SESSION_KEY) ||
      localStorage.getItem(HIRE_WIZARD_CONTEXT_SESSION_KEY);
    if (rawContext) {
      const parsed = JSON.parse(rawContext) as Partial<HireWizardPersistedContext>;
      if (
        parsed.websiteUrl &&
        normalizeWizardWebsiteKey(parsed.websiteUrl) === key &&
        parsed.stepId &&
        HIRE_WIZARD_STEPS.some((s) => s.id === parsed.stepId)
      ) {
        return parsed.stepId;
      }
    }
  } catch {
    /* ignore */
  }

  return "company";
}

/** @deprecated Use readHireWizardStepForWebsite */
export function readHireWizardStep(): HireWizardStepId {
  return "company";
}

export function writeHireWizardContext(
  websiteUrl: string,
  stepId: HireWizardStepId,
): void {
  if (typeof window === "undefined") return;
  try {
    const context: HireWizardPersistedContext = {
      websiteUrl: normalizeWizardWebsiteKey(websiteUrl),
      stepId,
    };
    const raw = JSON.stringify(context);
    sessionStorage.setItem(HIRE_WIZARD_CONTEXT_SESSION_KEY, raw);
    sessionStorage.setItem(HIRE_WIZARD_STEP_SESSION_KEY, stepId);
    localStorage.setItem(HIRE_WIZARD_CONTEXT_SESSION_KEY, raw);
    localStorage.setItem(HIRE_WIZARD_STEP_SESSION_KEY, stepId);
  } catch {
    /* ignore */
  }
}

export function writeHireWizardStep(stepId: HireWizardStepId): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(HIRE_WIZARD_STEP_SESSION_KEY, stepId);
  } catch {
    /* ignore */
  }
}

export function nextWizardStep(stepId: HireWizardStepId): HireWizardStepId | null {
  const index = hireWizardStepIndex(stepId);
  if (index < 0 || index >= HIRE_WIZARD_STEPS.length - 1) return null;
  return HIRE_WIZARD_STEPS[index + 1]!.id;
}

export function prevWizardStep(stepId: HireWizardStepId): HireWizardStepId | null {
  const index = hireWizardStepIndex(stepId);
  if (index <= 0) return null;
  return HIRE_WIZARD_STEPS[index - 1]!.id;
}
